import { useState } from "react"
import { Download, Eye, File, FileText, Mic, ShieldCheck, Upload } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { fileSize } from "@/lib/fileSize"
import { BRIEF_COPY } from "./copy"
import { REQUIREMENTS_ACCEPT, REQUIREMENTS_UPLOAD_COPY, useRequirementsUpload } from "./useRequirementsUpload"

const ICONS = { requirements: FileText, qa: ShieldCheck, qc: ShieldCheck, script: Mic, report: FileText }

function IconAction({ label, title = label, children, ...props }) {
  return (
    <Button type="button" variant="outline" size="icon" className="size-[34px] shrink-0 rounded-lg" title={title} aria-label={label} {...props}>
      {children}
    </Button>
  )
}

// One file on the order (design FileRow), in one of four states: a `file` to
// download, pasted `text` to read in place, a report that is `pending`, or
// `none` attached. The requirements row carries the tab's one write: its
// Upload, offered only while the order has no file and its customer may add
// one (the server's `canUpload`). It is the tab's one action, so unlike the
// design's icon-only button it carries its label.
function FileRow({ file, orderId }) {
  const [viewing, setViewing] = useState(false)
  const upload = useRequirementsUpload(orderId)
  const Icon = ICONS[file.kind] ?? File
  const empty = file.state === "pending" || file.state === "none"

  return (
    <div className="border-t border-border py-3 first:border-t-0 first:pt-0.5 last:pb-0.5">
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "grid size-[38px] shrink-0 place-items-center rounded-[10px] border",
            empty ? "border-dashed border-line-2 text-ink-3" : "border-border bg-accent text-muted-foreground"
          )}
        >
          <Icon className="size-[18px]" strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] leading-[1.3] font-bold text-foreground">{file.label}</div>
          <div className="mt-0.5 truncate text-[12px] text-ink-3">
            {file.state === "file" && (
              <>
                <b className="font-semibold text-muted-foreground">{file.fileName}</b>
                {[fileSize(file.fileSize), file.uploadedAt].filter(Boolean).map((part) => ` · ${part}`)}
              </>
            )}
            {file.state === "text" && BRIEF_COPY.pastedText}
            {file.state === "pending" && BRIEF_COPY.notAttachedYet}
            {file.state === "none" && BRIEF_COPY.noneAttached}
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {file.state === "file" && (
            <IconAction label={`${BRIEF_COPY.download} ${file.label}`} title={BRIEF_COPY.download} asChild>
              <a href={file.url} download={file.fileName}>
                <Download className="size-[15px]" strokeWidth={2} />
              </a>
            </IconAction>
          )}
          {file.state === "text" && (
            <Button type="button" variant="outline" size="sm" aria-expanded={viewing} onClick={() => setViewing((current) => !current)}>
              <Eye className="size-[15px]" strokeWidth={2} />
              {viewing ? BRIEF_COPY.hide : BRIEF_COPY.view}
            </Button>
          )}
          {file.canUpload && (
            <>
              <input
                ref={upload.inputRef}
                type="file"
                accept={REQUIREMENTS_ACCEPT}
                onChange={upload.onChange}
                className="sr-only"
                tabIndex={-1}
                aria-label="Choose a requirements file"
              />
              <Button type="button" variant="outline" size="sm" title={REQUIREMENTS_UPLOAD_COPY.helper} disabled={upload.saving} onClick={upload.choose}>
                <Upload className="size-[15px]" strokeWidth={2} />
                {upload.saving ? REQUIREMENTS_UPLOAD_COPY.uploading : REQUIREMENTS_UPLOAD_COPY.upload}
              </Button>
            </>
          )}
        </div>
      </div>
      {viewing && (
        <div className="mt-3 ml-[50px] max-h-60 overflow-auto rounded-[10px] border border-border bg-accent px-[14px] py-3 text-[13px] leading-[1.6] whitespace-pre-line text-muted-foreground max-[560px]:ml-0">
          {file.text}
        </div>
      )}
      {upload.error && <p role="alert" className="mt-2 text-xs font-medium text-primary">{upload.error}</p>}
    </div>
  )
}

export default function FileRows({ files = [], orderId }) {
  return (
    <div>
      {files.map((file) => (
        <FileRow key={file.label} file={file} orderId={orderId} />
      ))}
    </div>
  )
}
